import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { Card } from "../UI/Card.tsx";
import { OutlinedButton } from "../UI/OutlinedButton.tsx";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { useToast } from "../UI/Feedback/useToast.ts";
import {
  OnboardingLiveRestriction,
  OnboardingLiveStatus,
  UserFragmentFragment,
} from "../gql/graphql.ts";
import { useUpdateUser } from "./useUpdateUser.ts";
import { onboardingLiveDetailPathOf } from "../OnboardingLive/OnboardingLivePaths.ts";

function OnboardingLiveRestrictionBadge({
  restriction,
}: {
  restriction: OnboardingLiveRestriction;
}) {
  switch (restriction) {
    case OnboardingLiveRestriction.WholePlatform:
      return (
        <span
          className={
            "rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800"
          }
        >
          Piattaforma bloccata in attesa della live
        </span>
      );
    case OnboardingLiveRestriction.GroupLessonsOnly:
      return (
        <span
          className={
            "rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800"
          }
        >
          Lezioni di gruppo bloccate in attesa della live
        </span>
      );
    case OnboardingLiveRestriction.None:
      return (
        <span
          className={
            "rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800"
          }
        >
          Piattaforma sbloccata
        </span>
      );
  }
}

function statusLabelOf(status?: OnboardingLiveStatus | null): string {
  switch (status) {
    case OnboardingLiveStatus.ToBeDone:
      return "Da fare";
    case OnboardingLiveStatus.Completed:
      return "Completata";
    default:
      return "Non richiesta (utente storico o gruppo senza live)";
  }
}

function formatDateTime(date?: string | null): string {
  return date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "-";
}

export function UserOnboardingLiveCard({
  user,
  onChanged,
}: {
  user: UserFragmentFragment;
  onChanged: () => Promise<unknown>;
}) {
  const askConfirmation = useConfirm();
  const showToast = useToast();
  const { updateUser, loading } = useUpdateUser();

  async function changeStatus(status: OnboardingLiveStatus) {
    const confirmed = await askConfirmation({
      title: `Segnare la live onboarding come "${statusLabelOf(status)}"?`,
      message:
        status === OnboardingLiveStatus.ToBeDone
          ? "L'utente dovrà prenotare e partecipare a una live onboarding prima di poter usare di nuovo la piattaforma (se il suo user group lo prevede)."
          : "L'utente non dovrà più partecipare a una live onboarding.",
      confirmLabel: "Conferma",
    });
    if (!confirmed) return;
    try {
      await updateUser({ userId: user.id, onboardingLiveStatus: status });
      await onChanged();
      showToast("Stato della live onboarding aggiornato");
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  return (
    <Card>
      <div className={"space-y-4"}>
        <div className={"flex items-center justify-between gap-2"}>
          <h2 className={"font-bold text-lg"}>Live onboarding</h2>
          <OnboardingLiveRestrictionBadge
            restriction={user.onboardingLiveRestriction}
          />
        </div>
        <div className={"grid grid-cols-1 md:grid-cols-2 gap-2 text-sm"}>
          <p>
            Stato:{" "}
            <span className={"font-semibold"}>
              {statusLabelOf(user.onboardingLiveStatus)}
            </span>
          </p>
          <p>
            Completata il:{" "}
            <span className={"font-semibold"}>
              {formatDateTime(user.onboardingLiveCompletedAt)}
            </span>
            {user.completedOnboardingLiveId ? (
              <>
                {" · "}
                <Link
                  className={"underline"}
                  to={onboardingLiveDetailPathOf(
                    user.completedOnboardingLiveId,
                  )}
                >
                  vedi live
                </Link>
              </>
            ) : null}
          </p>
          <p>
            Ultima modifica:{" "}
            <span className={"font-semibold"}>
              {formatDateTime(user.onboardingLiveStatusUpdatedAt)}
            </span>
          </p>
          <p>
            Modificato da:{" "}
            <span className={"font-semibold"}>
              {user.onboardingLiveStatusUpdatedByEmail ??
                (user.onboardingLiveStatusUpdatedAt ? "Sistema" : "-")}
            </span>
          </p>
        </div>
        <div className={"flex flex-wrap gap-2"}>
          {user.onboardingLiveStatus !== OnboardingLiveStatus.ToBeDone ? (
            <OutlinedButton
              loading={loading}
              label={"Segna come da fare"}
              onClick={() => changeStatus(OnboardingLiveStatus.ToBeDone)}
            />
          ) : null}
          {user.onboardingLiveStatus !== OnboardingLiveStatus.Completed ? (
            <OutlinedButton
              loading={loading}
              label={"Segna come completata"}
              onClick={() => changeStatus(OnboardingLiveStatus.Completed)}
            />
          ) : null}
        </div>
      </div>
    </Card>
  );
}
